import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { MASTER_API_URL } from './constants'
import { getCurrentUser } from 'modules/common/helpers'
import { getFromLocalStorage, removeFromLocalStorage, saveToLocalStorage } from 'utils/storage'
import { LOCAL_STORAGE_KEYS } from 'modules/common/constants/storageKeys'
import { TokenResponse } from './auth/types/response'
import { store } from 'store'
import { logout } from 'store/slices/auth/thunks'
import dayjs, { extend } from 'dayjs'
import dayjsPluginUTC from 'dayjs/plugin/utc'
import { getLanguageCodeByName } from 'utils/languageCodes'
import i18n from 'i18next'
import { setCurrentUser } from 'store/slices/user/slice'

extend(dayjsPluginUTC)

export const REFRESH_TOKEN_RESPONSE_TYPE = {
  SUCCESS: 0,
  FAILED: 1,
  USER_DOES_NOT_EXIST: 2,
  USER_DEACTIVATED: 3,
}

const orgConfig = {
  baseURL: getFromLocalStorage(LOCAL_STORAGE_KEYS.ORG_URL),
}

const masterConfig = {
  baseURL: MASTER_API_URL,
}

const messagingConfig = {
  baseURL: getFromLocalStorage(LOCAL_STORAGE_KEYS.MESSAGING_URL),
}

let refreshTokenPromise: Promise<any> | null = null

export const authInstanceOrg: AxiosInstance = axios.create(orgConfig)
export const unauthInstanceOrg: AxiosInstance = axios.create(orgConfig)

export const authInstanceMessaging: AxiosInstance = axios.create(messagingConfig)
export const unauthInstanceMessaging: AxiosInstance = axios.create(messagingConfig)

export const authInstanceMaster: AxiosInstance = axios.create(masterConfig)
export const unauthInstanceMaster: AxiosInstance = axios.create(masterConfig)

const saveToken = (
  access_token: {
    token: string
    expiresIn: number
  },
  refresh_token: string
) => {
  saveToLocalStorage(LOCAL_STORAGE_KEYS.TOKEN, access_token)
  saveToLocalStorage(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, refresh_token)
}

const destroyToken = () => {
  removeFromLocalStorage(LOCAL_STORAGE_KEYS.TOKEN)
  removeFromLocalStorage(LOCAL_STORAGE_KEYS.REFRESH_TOKEN)
}

const changeLanguage = (supportedLanguages: any[], languageId: number) => {
  const userLanguage = supportedLanguages.find((lang) => lang.id === languageId)?.name

  if (userLanguage) {
    const langCode = getLanguageCodeByName(userLanguage)

    saveToLocalStorage(LOCAL_STORAGE_KEYS.USER_LANGUAGE, JSON.stringify(langCode))
    i18n.changeLanguage(langCode)
  }
}

const refreshToken = async (): Promise<string> => {
  const { credentialsInfo } = getCurrentUser()
  const { dispatch } = store

  if (refreshTokenPromise) {
    return refreshTokenPromise
  }

  refreshTokenPromise = authInstanceOrg
    .post<{
      responseType: number
      userCredentials: TokenResponse
    }>(`/authorization/refreshtokenMobile`, {
      accessToken: getFromLocalStorage(LOCAL_STORAGE_KEYS.TOKEN),
      refreshToken: getFromLocalStorage(LOCAL_STORAGE_KEYS.REFRESH_TOKEN),
      credentialsInfo: {
        organizationId: credentialsInfo.organizationId,
        identifier: credentialsInfo.identifier,
      },
    })
    .then((response) => {
      const {
        data: { responseType, userCredentials },
      } = response

      if (responseType === REFRESH_TOKEN_RESPONSE_TYPE.SUCCESS) {
        dispatch(setCurrentUser(userCredentials))
        saveToken(userCredentials.accessToken, userCredentials.refreshToken)
        changeLanguage(
          userCredentials.credentialsInfo.supportedLanguages,
          userCredentials.credentialsInfo.languageId
        )
        return Promise.resolve(userCredentials.accessToken)
      }

      if (responseType !== REFRESH_TOKEN_RESPONSE_TYPE.SUCCESS) {
        throw 'Refresh token failed'
      }
    })
    .catch((error) => {
      destroyToken()
      dispatch(logout())
      return Promise.reject(error)
    })
  return refreshTokenPromise
}

const handle401Error = async (error: any, instance: AxiosInstance) => {
  const originalRequest = error.config

  if (error.response && error.response.status === 401 && !originalRequest._retry) {
    try {
      await refreshToken()
      originalRequest._retry = true
      return instance(originalRequest)
    } catch (err) {
      return Promise.reject(err)
    } finally {
      refreshTokenPromise = null
    }
  }

  return Promise.reject(error)
}

const setDefaultHeaders = (config: AxiosRequestConfig) => {
  const access_token = getFromLocalStorage(LOCAL_STORAGE_KEYS.TOKEN)
  if (access_token) {
    config.headers.Authorization = `Bearer ${access_token.token}`
    config.headers['X-Timezone-Offset'] = dayjs().utcOffset()
    return config
  }
  return config
}

authInstanceOrg.interceptors.response.use(
  (res) => res,
  (error) => handle401Error(error, authInstanceOrg)
)

authInstanceOrg.interceptors.request.use(setDefaultHeaders)

authInstanceMessaging.interceptors.response.use(
  (res) => res,
  (error) => handle401Error(error, authInstanceMessaging)
)

authInstanceMessaging.interceptors.request.use(setDefaultHeaders)

authInstanceMaster.interceptors.response.use(
  (res) => res,
  (error) => handle401Error(error, authInstanceMaster)
)

authInstanceMaster.interceptors.request.use(setDefaultHeaders)
